import { useEffect, useRef, useState } from "react";

/**
 * Has this screen gone quiet? True after `idleMs` with no pointer, touch or key
 * activity, false again the instant any arrives. Listeners are attached once and torn
 * down on unmount, so there are no leaks across re-renders.
 *
 * Was `useIdleCursor`, which is what it did rather than what it asks. Two things fade
 * on a quiet screen now — the cursor at one delay, the wall's price badges at a longer
 * one — and they are the same question asked twice with different patience, so the
 * hook takes the delay and the caller names the meaning. Each call keeps its own timer;
 * they are independent by design.
 *
 * NOTE it hears only LOCAL input. A display driven entirely from a phone remote never
 * wakes it, which is correct for the cursor (there is no cursor to hide) and is a
 * deliberate choice for anything else: a wall left alone in a room settles down and
 * stays that way until somebody touches THAT screen.
 */
export function useScreenIdle(idleMs = 3000): boolean {
  const [hidden, setHidden] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const arm = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setHidden(true), idleMs);
    };
    const wake = () => {
      setHidden((prev) => (prev ? false : prev));
      arm();
    };

    window.addEventListener("mousemove", wake, { passive: true });
    window.addEventListener("mousedown", wake, { passive: true });
    window.addEventListener("touchstart", wake, { passive: true });
    window.addEventListener("keydown", wake);
    arm();

    return () => {
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("mousedown", wake);
      window.removeEventListener("touchstart", wake);
      window.removeEventListener("keydown", wake);
    };
  }, [idleMs]);

  return hidden;
}
